import Button from "@mui/material/Button";
import { Formik } from "formik"; // DUMMY CUSTOM DATA
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import Grid from "@mui/material/Grid";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import * as yup from "yup";
import { useCardSectionContext } from "../../context";
import { CardSectionStepDetailsProps } from "..";
import ShippingForm from "./shipping-form";
import StoreForm from "./store-form";
import PromotionForm from "./promotion-form";
import clientFetcher, { PredictCodPayload, PromotionResultType } from "src/lib/fetcher/client";
import { useGlobalContext } from "src/lib/components/salify-next-layout/default/global-context";

type CheckoutFormProps = Partial<CardSectionStepDetailsProps> & {
  readOnly?: boolean;
  promotionInSidebar?: boolean;
  leadingContent?: ReactNode;
  trailingContent?: ReactNode;
  renderSidebar?: (promotion: ReactElement) => ReactNode;
};

const CheckoutForm = (props: CheckoutFormProps) => {
  const router = useRouter();
  const { formValue, updateFormValue, provinces, districts, wards } = useCardSectionContext();
  const { updatePredictCostOnDeliveryResult, getPrice, promotionResult } = useGlobalContext();
  const [sameAsShipping, setSameAsShipping] = useState(false);
  const [shouldGetCodPrice, setShouldGetCodPrice] = useState(false);

  const handleFormSubmit = async (values: any) => {
    updateFormValue(values);
    props.nextUrl && router.push(props.nextUrl);
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={checkoutSchema}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setValues,
      }) => {
        const handleCheckboxChange = (checked: any) => {
          setSameAsShipping(checked);
          setFieldValue("same_as_shipping", checked);
        };

        /* eslint-disable */
        useEffect(() => {
          setSameAsShipping(formValue.same_as_shipping ?? false);
          setValues({ ...formValue } as any);
          var shippingPrice = getPrice().shipping;
          if (
            shippingPrice === 0 &&
            !(
              promotionResult &&
              promotionResult.code &&
              promotionResult.code.length &&
              promotionResult.resultType === PromotionResultType.FreeShipping
            )
          ) {
            setShouldGetCodPrice(true);
          }
        }, []);

        useEffect(() => {
          if (
            !shouldGetCodPrice ||
            !values.shipping_province ||
            !values.shipping_district ||
            !values.shipping_ward
          ) {
            return;
          }
          const province = provinces.find((x) => x.id === values.shipping_province);
          const district = districts.find((x) => x.id === values.shipping_district);
          const ward = wards.find((x) => x.id === values.shipping_ward);
          if (!province || !district || !ward) {
            return;
          }
          const payload: PredictCodPayload = {
            toDistrictCode: district.code,
            toProvinceCode: province.code,
            toWardCode: ward.code,
            storeId: values.shipping_store,
          };
          clientFetcher.predictCod(payload).then(async (result) => {
            if (result.requestIdentifier && result.requestIdentifier.length) {
              updatePredictCostOnDeliveryResult(result);
              setValues({ ...values, shipping_store: result.requestIdentifier } as any);
            }
          });
        }, [
          // values.shipping_store, // TODO need re-check on store changed
          values.shipping_province,
          values.shipping_district,
          values.shipping_ward,
          shouldGetCodPrice,
        ]);

        /* eslint-enable */
        const promotionForm = (
          <PromotionForm
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            readOnly={props.readOnly}
          />
        );

        const checkoutActions = props.readOnly ? null : (
          <Grid container spacing={6}>
            <Grid item sm={6} xs={12}>
              <Button
                LinkComponent={Link}
                variant="outlined"
                color="primary"
                type="button"
                href={props.backUrl}
                fullWidth
              >
                <SalifyFallback.Label path="Back to Cart" default="Quay lại" />
              </Button>
            </Grid>

            <Grid item sm={6} xs={12}>
              <Button variant="contained" color="primary" type="submit" fullWidth>
                <SalifyFallback.Label path="Proceed to Payment" default="Tiếp tục" />
              </Button>
            </Grid>
          </Grid>
        );

        const formBody = (
          <form onSubmit={handleSubmit}>
            <StoreForm
              values={values}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              readOnly={props.readOnly}
            />
            <ShippingForm
              values={values}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              readOnly={props.readOnly}
            />

            {!props.promotionInSidebar ? promotionForm : null}

            {/* <BillingAddressForm
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              sameAsShipping={sameAsShipping}
              setFieldValue={setFieldValue}
              touched={touched}
              values={values}
              readOnly={props.readOnly}
            /> */}

            {checkoutActions}
          </form>
        );

        if (props.promotionInSidebar) {
          return (
            <>
              <Grid item md={8} xs={12}>
                {props.leadingContent}
                {formBody}
                {props.trailingContent}
              </Grid>
              <Grid item md={4} xs={12}>
                {props.renderSidebar ? props.renderSidebar(promotionForm) : null}
              </Grid>
            </>
          );
        }

        return <>{formBody}</>;
      }}
    </Formik>
  );
};

const initialValues = {
  shipping_fullName: "",
  shipping_email: "",
  shipping_phone: "",
  shipping_store: "",
  shipping_province: "",
  shipping_district: "",
  shipping_ward: "",
  shipping_address: "",
  billing_fullName: "",
  billing_email: "",
  billing_phone: "",
  billing_province: "",
  billing_district: "",
  billing_ward: "",
  billing_address: "",
  same_as_shipping: false,
};

const checkoutSchema = yup.object().shape({
  shipping_fullName: yup.string().required("Họ và Tên là bắt buộc"),
  shipping_email: yup.string().email("Email không hợp lệ").required("Email là bắt buộc"),
  shipping_phone: yup.string().required("Số điện thoại là bắt buộc"),
  shipping_store: yup.string().required("Cửa hàng là bắt buộc"),
  shipping_province: yup.number().required("Tỉnh thành là bắt buộc"),
  shipping_district: yup.number().required("Quận/thành phố là bắt buộc"),
  shipping_ward: yup.number().required("Phường là bắt buộc"),
  shipping_address: yup.string().required("Địa chỉ là bắt buộc"),
  // billing_fullName: yup.string().required("Họ và Tên là bắt buộc"),
  // billing_email: yup.string().email("Email không hợp lệ").required("Email là bắt buộc"),
  // billing_phone: yup.string().required("Số điện thoại là bắt buộc"),
  // billing_province: yup.number().required("Tỉnh thành là bắt buộc"),
  // billing_district: yup.number().required("Quận/thành phố là bắt buộc"),
  // billing_ward: yup.number().required("Phường là bắt buộc"),
  // billing_address: yup.string().required("Địa chỉ là bắt buộc"),
});
export default CheckoutForm;
